import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	Alert,
	useMediaQuery,
	useTheme,
	CircularProgress,
} from '@mui/material';

import Button from '../../../common/buttons/Button';
import { DotsStepper } from '../../../common/DotsStepper';
import { AppDispatch, RootState } from '../../../../data/store';
import { showAlert } from '../../../../data/stores/alertStore';
import {
	onboardingAuthSuccess,
	onboardingAuthError,
	updateOnboardingPassword,
	clearOnboardingAuth,
} from '../../../../data/stores/authStore';
import {
	verifyOnboardingToken,
	refreshOnboardingSession,
	updateOnboardingProgress,
	submitOnboardingFormExternal,
} from '../../../../services/company-external-user.service';
import {
	OnboardingTokenVerificationResponse,
	CompanyExternalUser,
} from '../../../../data/models/companyExternalUser';
import * as GoogleStorageService from '../../../../services/google-storage.service';
import { processImageForUpload } from '../../../../utils/image-processing';

import { OnboardingStatus, OnboardingStep, FormValues } from './types';
import { steps } from './constants';
import { useCrewForm } from '../../hooks/useCrewForm';
import BasicInformationStep from './BasicInformationStep';
import ProfessionalDetailsStep from './ProfessionalDetailsStep';
import WorkLogisticsStep from './WorkLogisticsStep';
import VehicleTravelStep from './VehicleTravelStep';
import TravelPreferencesStep from './TravelPreferencesStep';
import RepresentationStep from './RepresentationStep';
import ReviewStep from './ReviewStep';
import PasswordEntryStep from './PasswordEntryStep';
import WelcomeStep from './WelcomeStep';
import { LoadingStatus, SuccessStatus, ErrorStatus } from './status';
import { v4 as uuidv4 } from 'uuid';
import {
	buildExternalSavePayload,
	buildExternalSubmitPayload,
	createExternalDefaultValues,
	mapExternalUserToFormValues,
	isFormValid,
} from './formUtils';
import OrbitoLogo from '../../../../images/Orbito_Logo_WhiteText.svg';
import OrbitoLogoDarkText from '../../../../images/Orbito_Logo_DarkText.svg';
import {
	ContentContainer,
	ScrollableContent,
	StickyHeader,
	FixedButtonsContainer,
	Container,
	StyledPaper,
	LogoContainer,
	LogoImage,
	MainContentContainer,
	HeaderFlexContainer,
	LogoutButton,
	PrimaryActionButton,
	StepperContainer,
	NavigationButtonsContainer,
	InnerNavigationButtons,
	ContentArea,
	PaperHeader,
} from './styles';
import { styled } from 'styled-components';

export default function OnboardingScreen() {
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch<AppDispatch>();
	const hasAttemptedRefresh = React.useRef(false);
	const hasInitializedStatus = React.useRef(false);
	const hasHydratedExternalUser = React.useRef(false);
	const hasCompletedSubmission = React.useRef(false);
	const scrollableRef = React.useRef<HTMLDivElement | null>(null);
	const theme = useTheme();
	const isLightMode = theme?.palette?.mode === 'light';
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const navigate = useNavigate();

	// Redux selectors for onboarding authentication
	const {
		onboardingToken,
		onboardingPassword,
		onboardingSessionToken,
		onboardingSessionExpiresAt,
		onboardingExternalUser,
		isOnboardingAuthenticated,
	} = useSelector((state: RootState) => state.authState);

	// Initialize status based on Redux state
	const [status, setStatus] = useState<OnboardingStatus>(
		isOnboardingAuthenticated
			? OnboardingStatus.WELCOME
			: OnboardingStatus.VERIFYING
	);
	const [currentStep, setCurrentStep] = useState<OnboardingStep>(
		OnboardingStep.BASIC_INFO
	);
	const [error, setError] = useState<string>('');
	const [isSavingProgress, setIsSavingProgress] = useState<boolean>(false);

	// Use Redux state for external user and token/password
	const externalUser = onboardingExternalUser;
	const password = onboardingPassword;
	const [passwordError, setPasswordError] = useState<string>('');

	const externalDefaultValues = React.useMemo(
		() => createExternalDefaultValues(),
		[]
	);

	const handleExternalSave = React.useCallback(
		async (payload: any) => {
			if (!payload || Object.keys(payload).length === 0) {
				return;
			}
			await updateOnboardingProgress(payload, onboardingSessionToken);
		},
		[onboardingSessionToken]
	);

	const handleExternalSubmit = React.useCallback(
		async (
			payload: any,
			{
				setStatus: setFormStatus,
				setError: setFormError,
				handleSessionExpired,
			}: {
				setStatus: (status: OnboardingStatus) => void;
				setError: (error: string) => void;
				handleSessionExpired: () => void;
			}
		) => {
			setFormStatus(OnboardingStatus.SUBMITTING);
			try {
				const response = await submitOnboardingFormExternal(
					payload,
					onboardingSessionToken
				);

				if (response.success) {
					setFormStatus(OnboardingStatus.SUCCESS);
					dispatch(clearOnboardingAuth());
					dispatch(
						showAlert({
							message:
								response.message ||
								'Onboarding completed successfully!',
							severity: 'success',
						})
					);
				} else {
					setFormError(
						response.error || 'Failed to submit onboarding form'
					);
					setFormStatus(OnboardingStatus.FORM);
				}
			} catch (err: any) {
				console.error('Error submitting onboarding form:', err);
				// Check if session expired (401 Unauthorized)
				if (err?.response?.status === 401 && handleSessionExpired) {
					handleSessionExpired();
				} else {
					setFormError('Failed to submit form. Please try again.');
					setFormStatus(OnboardingStatus.FORM);
				}
			}
		},
		[dispatch, onboardingSessionToken]
	);

	// Use custom hook for form logic
	const {
		control,
		errors,
		setValue,
		getValues,
		formValues,
		trigger,
		validateForm,
		saveProgress,
		submitForm,
		hydrateForm,
	} = useCrewForm<
		FormValues,
		CompanyExternalUser,
		any,
		any,
		string,
		{
			setStatus: (status: OnboardingStatus) => void;
			setError: (error: string) => void;
			handleSessionExpired: () => void;
		}
	>({
		defaultValues: externalDefaultValues,
		mode: 'onSubmit',
		reValidateMode: 'onBlur',
		hydrate: mapExternalUserToFormValues,
		buildSavePayload: (values, helpers) =>
			buildExternalSavePayload(values, helpers.isFieldDirty),
		onSave: handleExternalSave,
		buildSubmitPayload: buildExternalSubmitPayload,
		onSubmit: handleExternalSubmit,
		isFormValid: (values, args) => isFormValid(values, args),
	});

	useEffect(() => {
		if (status === OnboardingStatus.SUCCESS) {
			hasCompletedSubmission.current = true;
		}
	}, [status]);

	useEffect(() => {
		if (hasCompletedSubmission.current) {
			return;
		}

		// During verification/submission flows, avoid overriding status (except for initial mount)
		if (
			status === OnboardingStatus.SUBMITTING ||
			(hasInitializedStatus.current &&
				status === OnboardingStatus.VERIFYING)
		) {
			return;
		}

		if (!externalUser && hasHydratedExternalUser.current) {
			hasHydratedExternalUser.current = false;
		}

		// If already authenticated via Redux, proceed to welcome screen (only transition once)
		if (isOnboardingAuthenticated && externalUser) {
			if (!hasHydratedExternalUser.current) {
				hydrateForm(externalUser);
				hasHydratedExternalUser.current = true;
			}
			if (
				status !== OnboardingStatus.WELCOME &&
				status !== OnboardingStatus.FORM
			) {
				setStatus(OnboardingStatus.WELCOME);
			}
			hasInitializedStatus.current = true;
			return;
		}

		const tokenParam = searchParams.get('token');
		if (!tokenParam) {
			setError('No onboarding token provided');
			if (status !== OnboardingStatus.ERROR) {
				setStatus(OnboardingStatus.ERROR);
			}
			hasInitializedStatus.current = true;
			return;
		}

		// Go directly to password entry screen - we'll verify token + password together
		if (status !== OnboardingStatus.PASSWORD_ENTRY) {
			setStatus(OnboardingStatus.PASSWORD_ENTRY);
		}
		hasInitializedStatus.current = true;
	}, [
		status,
		searchParams,
		isOnboardingAuthenticated,
		externalUser,
		hydrateForm,
	]);

	// Refresh session once when a session token exists
	useEffect(() => {
		if (!isOnboardingAuthenticated || !onboardingSessionToken) return;
		if (hasAttemptedRefresh.current) return;
		hasAttemptedRefresh.current = true;
		(async () => {
			try {
				const refreshed = await refreshOnboardingSession(
					onboardingSessionToken
				);
				if (refreshed?.valid && refreshed?.sessionToken) {
					dispatch(
						onboardingAuthSuccess({
							token: onboardingToken,
							password: onboardingPassword,
							sessionToken: refreshed.sessionToken,
							expiresAt: refreshed.expiresAt,
							externalUser:
								refreshed.externalUser || externalUser,
						})
					);
				} else {
					dispatch(clearOnboardingAuth());
					setStatus(OnboardingStatus.PASSWORD_ENTRY);
				}
			} catch (e) {
				dispatch(clearOnboardingAuth());
				setStatus(OnboardingStatus.PASSWORD_ENTRY);
			}
		})();
	}, [isOnboardingAuthenticated, onboardingSessionToken]);

	// Ensure the scrollable content resets to top on step change
	useEffect(() => {
		if (scrollableRef.current) {
			scrollableRef.current.scrollTo({ top: 0, behavior: 'auto' });
		}
	}, [currentStep]);

	const handleProfilePhotoDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (!file) return;

			try {
				// Process the image (convert HEIC/HEIF and resize if needed)
				const { processedFile } = await processImageForUpload(file);

				const path = `${externalUser?.companyId}/profile-pictures/${processedFile.name}`;
				const response = await GoogleStorageService.uploadFile(
					processedFile,
					path,
					onboardingSessionToken
				);

				// Get a signed GET URL for displaying the uploaded image
				const signedGetUrl = await GoogleStorageService.getSignedGetUrl(
					path,
					onboardingSessionToken
				);

				// Update the response with the proper public URL
				const updatedResponse = {
					...response,
					publicUrl: signedGetUrl,
				};

				// Update form value with the GoogleStorageDocument object
				setValue('profilePictureUrl', updatedResponse);

				// Save progress to MongoDB after successful upload
				if (onboardingSessionToken) {
					await handleExternalSave({
						profilePictureUrl: updatedResponse,
					});
				}

				dispatch(
					showAlert({
						message: 'Profile picture uploaded successfully',
						severity: 'success',
					})
				);
			} catch (error) {
				console.error('Error uploading profile picture:', error);
				dispatch(
					showAlert({
						message: 'Error uploading profile picture',
						severity: 'error',
					})
				);
			}
		},
		[
			externalUser?.companyId,
			onboardingSessionToken,
			setValue,
			handleExternalSave,
			dispatch,
		]
	);

	const handleResumeDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			if (!acceptedFiles?.length) return;

			const currentUploads =
				getValues('resumeProjectHistoryUploads') || [];
			const maxNewFiles = 5 - currentUploads.length;

			if (maxNewFiles <= 0) {
				dispatch(
					showAlert({
						message:
							'Maximum of 5 resume/project history files allowed',
						severity: 'warning',
					})
				);
				return;
			}

			const filesToUpload = acceptedFiles.slice(0, maxNewFiles);
			const uploadedDocuments: any[] = [];

			for (const file of filesToUpload) {
				try {
					const path = `${externalUser?.companyId}/documents/${file.name}`;
					const response = await GoogleStorageService.uploadFile(
						file,
						path,
						onboardingSessionToken
					);

					uploadedDocuments.push(response);
				} catch (error) {
					console.error('Error uploading resume file:', error);
					dispatch(
						showAlert({
							message: `Error uploading ${file.name}`,
							severity: 'error',
						})
					);
				}
			}

			if (uploadedDocuments.length > 0) {
				const newUploads = [...currentUploads, ...uploadedDocuments];
				setValue('resumeProjectHistoryUploads', newUploads);

				// Save progress to MongoDB after successful uploads
				if (onboardingSessionToken) {
					await handleExternalSave({
						resumeProjectHistoryUploads: newUploads,
					});
				}

				dispatch(
					showAlert({
						message: `${uploadedDocuments.length} file(s) uploaded successfully`,
						severity: 'success',
					})
				);
			}
		},
		[
			externalUser?.companyId,
			onboardingSessionToken,
			setValue,
			getValues,
			handleExternalSave,
			dispatch,
		]
	);

	const handleKitDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			if (!acceptedFiles?.length) return;

			const currentUploads = getValues('kitDocuments') || [];
			const maxNewFiles = 5 - currentUploads.length;

			if (maxNewFiles <= 0) {
				dispatch(
					showAlert({
						message: 'Maximum of 5 kit documents allowed',
						severity: 'warning',
					})
				);
				return;
			}

			const filesToUpload = acceptedFiles.slice(0, maxNewFiles);
			const uploadedDocuments: any[] = [];

			for (const file of filesToUpload) {
				const uuid = uuidv4();
				try {
					const path = `${externalUser?.companyId}/kits/${uuid}_${file.name}`;
					const response = await GoogleStorageService.uploadFile(
						file,
						path,
						onboardingSessionToken
					);

					uploadedDocuments.push(response);
				} catch (error) {
					console.error('Error uploading kit file:', error);
					dispatch(
						showAlert({
							message: `Error uploading ${file.name}`,
							severity: 'error',
						})
					);
				}
			}

			if (uploadedDocuments.length > 0) {
				const newUploads = [...currentUploads, ...uploadedDocuments];
				setValue('kitDocuments', newUploads);

				// Save progress to MongoDB after successful uploads
				if (onboardingSessionToken) {
					await handleExternalSave({
						kitDocuments: newUploads,
					});
				}

				dispatch(
					showAlert({
						message: `${uploadedDocuments.length} kit file(s) uploaded successfully`,
						severity: 'success',
					})
				);
			}
		},
		[
			externalUser?.companyId,
			onboardingSessionToken,
			setValue,
			getValues,
			handleExternalSave,
			dispatch,
		]
	);

	const verifyTokenWithPassword = async (
		tokenValue: string,
		passwordValue: string
	) => {
		try {
			setPasswordError('');
			setStatus(OnboardingStatus.VERIFYING);

			const response: OnboardingTokenVerificationResponse =
				await verifyOnboardingToken({
					token: tokenValue,
					password: passwordValue,
				});

			if (response.valid && response.externalUser) {
				// Store authentication in Redux for persistence
				dispatch(
					onboardingAuthSuccess({
						token: tokenValue,
						password: passwordValue,
						sessionToken: response.sessionToken,
						expiresAt: response.expiresAt,
						externalUser: response.externalUser,
					})
				);

				// Hydrate full form from external user
				hydrateForm(response.externalUser);
				setStatus(OnboardingStatus.WELCOME);
			} else {
				// Clear any existing auth on error
				dispatch(
					onboardingAuthError(
						response.error ||
							'Invalid password. Please check your email and try again.'
					)
				);
				setPasswordError(
					response.error ||
						'Invalid password. Please check your email and try again.'
				);
				setStatus(OnboardingStatus.PASSWORD_ENTRY);
			}
		} catch (err) {
			console.error('Error verifying onboarding password:', err);
			dispatch(
				onboardingAuthError(
					'Failed to verify password. Please try again.'
				)
			);
			setPasswordError('Failed to verify password. Please try again.');
			setStatus(OnboardingStatus.PASSWORD_ENTRY);
		}
	};

	// Navigation handlers
	const handleNext = async () => {
		// Define validation fields for each step
		let validationFields: string[] = [];

		switch (currentStep) {
			case OnboardingStep.BASIC_INFO:
				validationFields = [
					'firstName',
					'lastName',
					'preferredName',
					'dateOfBirth',
					'email',
					'phone',
					'phoneCarrier',
					'emergencyContactFirstName',
					'emergencyContactLastName',
					'emergencyContactPhone',
					'homeAddress.line1',
					'homeAddress.city',
					'homeAddress.state',
					'homeAddress.zip',
				];
				break;
			case OnboardingStep.PROFESSIONAL_DETAILS:
				validationFields = [
					'unionStatus',
					'productionRole',
					'rate',
					'yearsOfExperiencePrimary',
					'rateType',
					'rateTenOrTwelveHours',
					'isRateFixed',
					'chargesOvertimeTypically',
					'workReferenceFirstName',
					'workReferenceLastName',
					'workReferenceEmail',
					'workReferencePhone',
				];
				break;
			case OnboardingStep.VEHICLE_TRAVEL:
				validationFields = [
					'homeAirport',
					'willingToTravelDomestically',
					'willingToTravelInternationally',
				];
				// Conditionally add driver's license validation
				if (!formValues.noDriversLicense) {
					validationFields.push('driversLicenseNumber');
				}
				break;
			case OnboardingStep.WORK_LOGISTICS:
				validationFields = [
					'localWorkLocations',
					'employmentTypePreference',
				];
				break;
			case OnboardingStep.TRAVEL_PREFERENCES:
			case OnboardingStep.REPRESENTATION:
			case OnboardingStep.REVIEW:
				// No required fields for these steps
				validationFields = [];
				break;
		}

		// Trigger validation for the current step
		const isValid = await trigger(validationFields as any);

		if (!isValid) {
			// Find the first field with an error and focus on it
			for (const field of validationFields) {
				if (errors[field]) {
					// Try to find and focus the input element
					const inputElement = document.querySelector(
						`[name="${field}"]`
					) as HTMLElement;
					if (inputElement) {
						inputElement.focus();
						// Scroll the element into view
						inputElement.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
						break;
					}
				}
			}
			return; // Don't proceed if validation fails
		}

		// Save current progress before moving to next step (session-based)
		if (onboardingSessionToken) {
			setIsSavingProgress(true);
			try {
				const saved = await saveProgress(onboardingSessionToken);
				if (saved) {
					dispatch(
						showAlert({
							message: 'Progress saved',
							severity: 'success',
						})
					);
				} else {
					setError(
						'Failed to save your progress. Your changes may not be saved. You can continue, but please try again or contact support if this persists.'
					);
				}
			} catch (error) {
				console.error('Error saving progress:', error);
				setError(
					'Failed to save your progress. Your changes may not be saved. You can continue, but please try again or contact support if this persists.'
				);
			} finally {
				setIsSavingProgress(false);
			}
		}

		if (currentStep < OnboardingStep.REVIEW) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleBack = () => {
		if (currentStep > OnboardingStep.BASIC_INFO) {
			setCurrentStep(currentStep - 1);
		}
	};

	// Password handling methods
	const handlePasswordSubmit = () => {
		const tokenParam = searchParams.get('token');
		if (!tokenParam) {
			setPasswordError('No token available');
			return;
		}

		if (!password || password.length !== 6) {
			setPasswordError('Please enter a 6-character password');
			return;
		}

		verifyTokenWithPassword(tokenParam, password);
	};

	const handlePasswordChange = (newPassword: string) => {
		dispatch(updateOnboardingPassword(newPassword));
		setPasswordError('');
	};

	const handleWelcomeStart = () => {
		setStatus(OnboardingStatus.FORM);
	};

	const onFormSubmit = async () => {
		if (!onboardingSessionToken) {
			setError('Missing session token');
			return;
		}

		await submitForm({
			setStatus,
			setError,
			handleSessionExpired,
		});
	};

	const handleSessionExpired = async () => {
		// Clear current session and go back to password entry
		dispatch(clearOnboardingAuth());
		setStatus(OnboardingStatus.PASSWORD_ENTRY);
		setError('Your session has expired. Please enter your password again.');
	};

	const renderPasswordEntry = () => (
		<PasswordEntryStep
			password={password}
			handlePasswordChange={handlePasswordChange}
			handlePasswordSubmit={handlePasswordSubmit}
			passwordError={passwordError}
			isVerifying={status === OnboardingStatus.VERIFYING}
		/>
	);

	const renderContent = () => {
		switch (status) {
			case OnboardingStatus.WELCOME:
				return (
					<WelcomeStep
						onStart={handleWelcomeStart}
						companyName={externalUser?.company?.name}
					/>
				);

			case OnboardingStatus.VERIFYING:
				return <LoadingStatus message="Verifying your invitation..." />;

			case OnboardingStatus.PASSWORD_ENTRY:
				return renderPasswordEntry();

			case OnboardingStatus.FORM:
				return (
					<ContentContainer>
						{/* Scrollable Content Area */}
						<ScrollableContent ref={scrollableRef}>
							{error && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{error}
								</Alert>
							)}

							{/* Step Content */}
							{currentStep === OnboardingStep.BASIC_INFO && (
								<BasicInformationStep
									token={onboardingSessionToken}
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
									onDrop={handleProfilePhotoDrop}
								/>
							)}
							{currentStep ===
								OnboardingStep.PROFESSIONAL_DETAILS && (
								<ProfessionalDetailsStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
									token={onboardingSessionToken}
									onDropResume={handleResumeDrop}
									onDropKit={handleKitDrop}
									onDocumentsUpdate={handleExternalSave}
								/>
							)}
							{currentStep === OnboardingStep.WORK_LOGISTICS && (
								<WorkLogisticsStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep === OnboardingStep.VEHICLE_TRAVEL && (
								<VehicleTravelStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep ===
								OnboardingStep.TRAVEL_PREFERENCES && (
								<TravelPreferencesStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep === OnboardingStep.REPRESENTATION && (
								<RepresentationStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep === OnboardingStep.REVIEW && (
								<ReviewStep
									formValues={formValues}
									control={control}
									setValue={setValue}
									errors={errors}
									token={onboardingSessionToken}
									onDocumentsUpdate={handleExternalSave}
								/>
							)}

							<FixedButtonsContainer>
								<NavigationButtonsContainer>
									{/* Navigation buttons */}
									<InnerNavigationButtons>
										{currentStep < OnboardingStep.REVIEW ? (
											<PrimaryActionButton
												variant="contained"
												onClick={handleNext}
												disabled={isSavingProgress}
												sx={{
													minWidth: isSavingProgress
														? '120px'
														: 'auto',
												}}
											>
												{isSavingProgress ? (
													<>
														<CircularProgress
															size={20}
															sx={{
																mr: 1,
																color: 'inherit',
															}}
														/>
														Saving...
													</>
												) : (
													'NEXT'
												)}
											</PrimaryActionButton>
										) : (
											<PrimaryActionButton
												variant="contained"
												onClick={onFormSubmit}
												disabled={
													!validateForm(currentStep)
												}
											>
												SUBMIT
											</PrimaryActionButton>
										)}
									</InnerNavigationButtons>
								</NavigationButtonsContainer>
							</FixedButtonsContainer>
						</ScrollableContent>
					</ContentContainer>
				);

			case OnboardingStatus.SUBMITTING:
				return (
					<LoadingStatus message="Submitting your information..." />
				);

			case OnboardingStatus.SUCCESS:
				return <SuccessStatus />;

			case OnboardingStatus.ERROR:
				return <ErrorStatus error={error} />;

			default:
				return null;
		}
	};

	return (
		<Container>
			{externalUser?.company?.name && (
				<CompanyText>
					On behalf of {externalUser?.company?.name}
				</CompanyText>
			)}
			<StyledPaper gradientBorder>
				<MainContentContainer>
					{/* Sticky Header - Hidden on Welcome Screen */}
					<StickyHeader>
						<HeaderFlexContainer>
							<LogoImage
								src={
									isLightMode
										? OrbitoLogoDarkText
										: OrbitoLogo
								}
								onClick={() => navigate('/')}
							/>
							{status === OnboardingStatus.FORM && (
								<LogoutButton
									variant="outlined"
									onClick={() => {
										dispatch(clearOnboardingAuth());
										setStatus(
											OnboardingStatus.PASSWORD_ENTRY
										);
										setError('');
										setPasswordError('');
									}}
								>
									Logout
								</LogoutButton>
							)}
						</HeaderFlexContainer>
						<PaperHeader>
							{status === OnboardingStatus.PASSWORD_ENTRY
								? 'Secure Access Verification'
								: 'Crew Onboarding Form'}
						</PaperHeader>
						{status === OnboardingStatus.FORM && (
							<StepperContainer>
								<DotsStepper
									totalSteps={7}
									activeStep={currentStep}
									showButtons={false}
									stepLabels={steps}
									onStepClick={(stepIndex) =>
										setCurrentStep(stepIndex)
									}
									onBack={handleBack}
								/>
							</StepperContainer>
						)}
					</StickyHeader>

					{/* Content Area */}
					<ContentArea>{renderContent()}</ContentArea>
				</MainContentContainer>
			</StyledPaper>
		</Container>
	);
}

const CompanyText = styled.div`
	position: absolute;
	top: 20px;
	font-size: 14px;
	color: ${({ theme }) => theme.palette.text.label};
	${({ theme }) => theme.breakpoints.down('sm')} {
		top: -30px;
	}
`;
